/**
 * Thu nhỏ ảnh ngay trên máy khách trước khi gửi lên máy chủ.
 *
 * Vì sao cần: quyết định 0040 bắt buộc cả 9 mục bảng kiểm đều có ảnh. Ở quy mô
 * 50 cơ sở × 2 ca × 9 ảnh là gần 900 ảnh/ngày; ảnh gốc điện thoại 3–8 MB thì
 * nhân viên đứng chờ mạng rất lâu và kho ảnh phình nhanh. Thu về cạnh dài
 * 1600px + JPEG chất lượng 0.82 thường còn 200–400 KB mà vẫn đọc được biển số,
 * mặt đồng hồ, số tiền trong két.
 *
 * Phụ thu ngoài lề: canvas re-encode luôn ra JPEG, nên ảnh HEIC do iPhone chọn
 * từ thư viện (Safari giải mã được) cũng thành JPEG — tránh được câu lỗi HEIC
 * của uploadImageToR2.
 *
 * Nguyên tắc AN TOÀN: mọi nhánh hỏng đều TRẢ LẠI FILE GỐC, không ném lỗi. Thu
 * nhỏ chỉ là tối ưu; làm mất ảnh vì tối ưu thất bại thì nhân viên kẹt không
 * đóng được ca — đúng cái mà việc chặn cứng khiến trở nên nghiêm trọng.
 */

/** Cạnh dài tối đa sau khi thu nhỏ. */
export const MAX_EDGE = 1600;

/** Ảnh nhỏ hơn ngưỡng này thì không đụng vào — re-encode chỉ làm xấu đi. */
export const SKIP_BELOW_BYTES = 300 * 1024;

const JPEG_QUALITY = 0.82;

/**
 * Kích thước đích, giữ nguyên tỉ lệ. Ảnh đã nhỏ hơn `max` thì giữ nguyên
 * (không phóng to). Hàm thuần tuý — tách riêng để kiểm được mà không cần
 * canvas, thứ jsdom không có.
 */
export function computeTargetSize(
  width: number,
  height: number,
  max: number = MAX_EDGE,
): { width: number; height: number } {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return { width: 0, height: 0 };
  }
  const longest = Math.max(width, height);
  if (longest <= max) return { width: Math.round(width), height: Math.round(height) };
  const ratio = max / longest;
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

/** Trình duyệt này có đủ đồ để vẽ lại ảnh không. */
function canRedraw(): boolean {
  if (typeof document === "undefined" || typeof createImageBitmap !== "function") return false;
  const c = document.createElement("canvas");
  return typeof c.getContext === "function" && typeof c.toBlob === "function";
}

/**
 * Thu nhỏ `file` về JPEG cạnh dài <= MAX_EDGE.
 *
 * Trả lại chính `file` khi: không phải ảnh, ảnh đã nhỏ, trình duyệt không vẽ
 * được, hoặc bất kỳ lỗi nào giữa chừng.
 */
export async function downscaleImage(file: File, max: number = MAX_EDGE): Promise<File> {
  if (!file.type.startsWith("image/")) return file;
  if (file.size <= SKIP_BELOW_BYTES) return file;
  if (!canRedraw()) return file;

  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = computeTargetSize(bitmap.width, bitmap.height, max);
    if (width === 0 || height === 0) {
      bitmap.close?.();
      return file;
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close?.();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
    );
    // Re-encode to ra file LỚN hơn (ảnh gốc vốn đã nén tốt) thì giữ bản gốc.
    if (!blob || blob.size >= file.size) return file;

    return new File([blob], renameToJpg(file.name), {
      type: "image/jpeg",
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  }
}

/** "IMG_0421.HEIC" → "IMG_0421.jpg". Tên rỗng thì đặt tên mặc định. */
export function renameToJpg(name: string): string {
  const base = name.replace(/\.[^.]+$/, "").trim();
  return (base || "anh-ca") + ".jpg";
}
